<template>
  <div v-if="visible" class="custom-dialog-overlay" @click="handleOverlayClick">
    <div class="custom-dialog" @click.stop>
      <div class="dialog-header">
        <h3 class="dialog-title">{{ title }}</h3>
      </div>
      
      <div class="dialog-content">
        <p class="dialog-message" v-html="message"></p>
      </div>
      
      <div class="dialog-actions">
        <button 
          v-if="showCancel" 
          class="dialog-btn cancel-btn" 
          @click="handleCancel"
        >
          {{ cancelText }}
        </button>
        <button 
          class="dialog-btn confirm-btn" 
          :class="{ 'disabled': !canConfirm }"
          :disabled="!canConfirm"
          @click="handleConfirm"
        >
          {{ confirmButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '提示'
    },
    message: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    showCancel: {
      type: Boolean,
      default: true
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: false
    },
    countdown: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentCountdown: 0,
      countdownTimer: null
    }
  },
  computed: {
    canConfirm() {
      return this.currentCountdown <= 0;
    },
    confirmButtonText() {
      if (this.currentCountdown > 0) {
        return `${this.confirmText} (${this.currentCountdown}s)`;
      }
      return this.confirmText;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.countdown > 0) {
        this.startCountdown();
      } else {
        this.clearCountdown();
      }
    }
  },
  methods: {
    startCountdown() {
      this.currentCountdown = this.countdown;
      this.countdownTimer = setInterval(() => {
        this.currentCountdown--;
        if (this.currentCountdown <= 0) {
          this.clearCountdown();
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    handleConfirm() {
      if (!this.canConfirm) return;
      this.$emit('confirm');
      this.$emit('update:visible', false);
    },
    handleCancel() {
      this.$emit('cancel');
      this.$emit('update:visible', false);
    },
    handleOverlayClick() {
      if (this.closeOnClickOverlay) {
        this.handleCancel();
      }
    }
  },
  beforeDestroy() {
    this.clearCountdown();
  }
};
</script>

<style lang="scss" scoped>
.custom-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.custom-dialog {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 320px;
  max-height: 60vh;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: dialogSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dialog-header {
  padding: 16px 20px 12px 20px;
  text-align: center;
  border-bottom: 1px solid #f5f5f5;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 0;
  line-height: 1.4;
}

.dialog-content {
  padding: 16px 20px 20px 20px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(70vh - 100px);
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
}

.dialog-message {
  font-size: 14px;
  line-height: 1.6;
  color: #666666;
  margin: 0;
  text-align: left;
  word-break: break-word;
}

.dialog-actions {
  padding: 12px 20px 16px 20px;
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  border-top: 1px solid #f0f0f0;
}

.dialog-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  
  &:active {
    transform: scale(0.98);
  }
}

.cancel-btn {
  background: #f5f5f5;
  color: #666666;
  
  &:hover {
    background: #e8e8e8;
  }
  
  &:active {
    background: #d9d9d9;
  }
}

.confirm-btn {
  background: linear-gradient(135deg, #ff4757 0%, #ff3742 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
  
  &:hover:not(.disabled) {
    box-shadow: 0 6px 16px rgba(255, 71, 87, 0.4);
    transform: translateY(-1px);
  }
  
  &:active:not(.disabled) {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 2px 8px rgba(255, 71, 87, 0.3);
  }
  
  &.disabled {
    background: #cccccc;
    color: #999999;
    box-shadow: none;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
    
    &:active {
      transform: none;
    }
  }
}

// 单按钮时的样式调整
.dialog-actions:has(.dialog-btn:only-child) {
  .dialog-btn {
    width: 100%;
  }
}
</style>
